/**
 * The webhook event taxonomy — a client-side mirror of
 * `KnowIt.Domain/Notifications/CommunicationEventTypes.cs`. An id absent from it 400s
 * server-side, so the picker and `isValidEventFilter` must stay exactly in step with it.
 * Ported verbatim from the owner-added `plugins/webhooks/client/api.ts` (§A.5 of the wave
 * spec). BRIEF-CGNI S2-6 regenerated it from `CommunicationEventTypes.All` (it had fallen behind:
 * no work, watch, question, code, mail, marketplace… events); the server test
 * `WebhookTaxonomyParityTests` now fails the moment the two differ.
 */

export interface WebhookEventGroup {
  id: string;
  events: string[];
}

export const WEBHOOK_EVENT_TAXONOMY: WebhookEventGroup[] = [
  {
    id: "documents",
    events: [
      "documents.publish",
      "documents.update",
      "documents.add_user",
      "documents.mentioned",
      "documents.nudge",
      // The one non-dotted legacy id.
      "document_chat_mention",
    ],
  },
  { id: "collections", events: ["collections.add_user", "collections.create"] },
  { id: "comments", events: ["comments.create", "comments.resolve", "comments.mentioned"] },
  { id: "revisions", events: ["revisions.create"] },
  {
    id: "emails",
    events: [
      "emails.invite_accepted",
      "emails.onboarding",
      "emails.features",
      "emails.export_completed",
      "emails.reminders",
    ],
  },
  {
    id: "runs",
    events: ["runs.completed", "runs.failed", "runs.approval_requested", "runs.paused"],
  },
  { id: "digests", events: ["digests.completed"] },
  {
    id: "pulse",
    events: ["pulse.decisions", "pulse.decisions.review", "pulse.data-health", "pulse.paper"],
  },
  { id: "flows", events: ["flows.published"] },
  { id: "tasks", events: ["tasks.fired"] },
  { id: "ide", events: ["ide.connected", "ide.disconnected"] },
  {
    id: "ide_access",
    events: [
      "ide_access.requested",
      "ide_access.approved",
      "ide_access.declined",
      "ide_access.revoked",
      "ide_access.expired",
    ],
  },
  { id: "security", events: ["security.authz_denied"] },
  {
    id: "marketplace",
    events: [
      "marketplace.inquiry_received",
      "marketplace.listing_published",
      "marketplace.saved_search_matches",
      "marketplace.listing_unpublished",
      "marketplace.quote_received",
      "marketplace.quote_accepted",
      "marketplace.quote_declined",
      "marketplace.message_received",
      "marketplace.review_received",
    ],
  },
  {
    id: "social",
    events: [
      "social.connection_requested",
      "social.connection_accepted",
      "social.post_commented",
      "social.post_liked",
      "social.skill_endorsed",
    ],
  },
  {
    id: "engagements",
    events: [
      "engagements.offered",
      "engagements.accepted",
      "engagements.declined",
      "engagements.ended",
    ],
  },
  {
    id: "workplan",
    events: ["workplan.part_assigned", "workplan.part_supervision", "workplan.part_delivered"],
  },
  {
    id: "milestones",
    events: [
      "milestones.proposed",
      "milestones.agreed",
      "milestones.delivered",
      "milestones.accepted",
      "milestones.paid",
      "milestones.disputed",
    ],
  },
  {
    id: "work",
    events: [
      "work.item.created",
      "work.item.assigned",
      "work.item.transitioned",
      "work.item.commented",
      "work.item.mentioned",
      "work.item.blocked",
      "work.item.unblocked",
      "work.question.asked",
      "work.question.answered",
      "work.question.amended",
      "work.question.overdue",
      "work.question.escalated",
      "work.question.defaulted",
      "work.question.stale",
      "work.sprint.started",
      "work.sprint.closed",
      "work.release.published",
      "work.incident.declared",
      "work.incident.resolved",
      "work.sla.breached",
      "work.sync.failed",
      "work.sync.conflict",
      "work.signal.orphaned",
    ],
  },
  { id: "mail", events: ["mail.alert"] },
  { id: "channels", events: ["channel.alert"] },
  { id: "watch", events: ["watch.fired", "watch.notified"] },
  { id: "question", events: ["question.changed"] },
  { id: "code", events: ["code.attention"] },
];

export const ALL_WEBHOOK_EVENTS: string[] = WEBHOOK_EVENT_TAXONOMY.flatMap((group) => group.events);

const ALL_EVENTS_LOWER = new Set(ALL_WEBHOOK_EVENTS.map((id) => id.toLowerCase()));

/**
 * The client twin of `CommunicationEventTypes.AllowsWildcard`: an id is valid if it is an
 * exact taxonomy member, the global `*`, or `prefix.*` where at least one taxonomy id starts
 * with `prefix.`. Case-insensitive, matching the server's own comparison.
 */
export function isValidEventFilter(id: string): boolean {
  const value = id.trim().toLowerCase();
  if (!value) {
    return false;
  }
  if (value === "*") {
    return true;
  }
  if (value.endsWith(".*")) {
    const prefix = value.slice(0, -1); // "runs.*" -> "runs."
    return ALL_WEBHOOK_EVENTS.some((event) => event.toLowerCase().startsWith(prefix));
  }
  return ALL_EVENTS_LOWER.has(value);
}

/** Which taxonomy group (if any) a wildcard id belongs to — `"runs.*"` → `"runs"`. */
export function groupIdForWildcard(id: string): string | null {
  if (!id.endsWith(".*")) {
    return null;
  }
  const prefix = id.slice(0, -2);
  return WEBHOOK_EVENT_TAXONOMY.some((group) => group.id === prefix) ? prefix : null;
}
